import { AfterViewInit, Component, Inject, OnDestroy } from '@angular/core';
import {
	MatDatepickerInputEvent,
	MatDatepickerInput,
	MatDatepickerToggle,
	MatDatepicker,
} from '@angular/material/datepicker';
import {
	MatDialogRef,
	MAT_DIALOG_DATA,
	MatDialogContent,
	MatDialogActions,
} from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Company } from 'src/app/models/data/company';
import { Device } from 'src/app/models/data/device';
import { Location } from 'src/app/models/data/location';
import { User } from 'src/app/models/data/user';
import { UserProfile } from 'src/app/models/model';
import {
	ApiService,
	QueryParam,
	ApiLoadingKeys,
} from 'src/app/services/api.services';
import {
	BehaviorSubject,
	Observable,
	of,
	interval,
	debounce,
	mergeMap,
	Subscription,
	from,
	map,
} from 'rxjs';
import { Client } from '../../../models/client';
import { UserProfileService } from '../../../services/user-profile.service';
import { MatIconButton, MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { LoadingIndicatorComponent } from '../../../components/loading-indicator/loading-indicator.component';
import { MultiSelectTypeaheadComponent } from '../../../components/multi-select-typeahead/multi-select-typeahead.component';
import { MatDivider } from '@angular/material/list';
import {
	MatFormField,
	MatLabel,
	MatSuffix,
} from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { FormsModule } from '@angular/forms';

export type AccessReportFilterOptions =
	Components.Schemas.AccessReportFilterOptions;

export type AccessReportFilters = {
	deviceIds?: { id: string; serialNumber: string }[];
	deviceOwnerCompanyIds?: { id: string; name: string }[];
	accessedByCompanyIds?: { id: string; name: string }[];
	accessedByUserIds?: {
		id: string;
		firstName: string;
		lastName: string;
		email: string;
		displayName: string;
	}[];
	propertyIds?: Location[];
	visitStartDate?: Date;
	visitEndDate?: Date;
	firstName?: Client[];
	lastName?: Client[];
	clientIdn?: Client[];
};

/** Noon local time so the calendar day survives `toISOString()`. */
export const defaultAccessReportStartDate = (today = new Date()): Date =>
	new Date(today.getFullYear(), today.getMonth() - 1, today.getDate(), 12);

export const emptyAccessReportFilters = (): AccessReportFilters => ({
	deviceIds: [],
	deviceOwnerCompanyIds: [],
	accessedByCompanyIds: [],
	accessedByUserIds: [],
	propertyIds: [],
	visitStartDate: undefined,
	visitEndDate: undefined,
	firstName: [],
	lastName: [],
	clientIdn: [],
});

interface SearchConfig {
	type:
		| 'device'
		| 'company'
		| 'user'
		| 'property'
		| 'deviceOwner'
		| 'firstName'
		| 'lastName'
		| 'clientIdn';
	searchEvents: BehaviorSubject<string>;
	subscription: Subscription;
	selectedItems: Set<any>;
	options: any[];
	apiCall: (params: QueryParam[]) => Promise<any>;
	mapResponse: (item: any) => any;
	updateFilters: (items: any[]) => void;
}

@Component({
	selector: 'app-access-report-dialog',
	templateUrl: './access-report-dialog.component.html',
	styleUrls: [
		'./access-report-dialog.component.scss',
		'../../common/main.scss',
	],
	imports: [
		MatIconButton,
		MatIcon,
		CdkScrollable,
		MatDialogContent,
		LoadingIndicatorComponent,
		MultiSelectTypeaheadComponent,
		MatDivider,
		MatFormField,
		MatLabel,
		MatInput,
		MatDatepickerInput,
		FormsModule,
		MatDatepickerToggle,
		MatSuffix,
		MatDatepicker,
		MatDialogActions,
		MatButton,
	],
})
export class AccessReportDialogComponent implements AfterViewInit, OnDestroy {
	accessReportFilters: AccessReportFilters = emptyAccessReportFilters();

	filterOptions?: AccessReportFilterOptions;

	apiLoadingKeys = ApiLoadingKeys;

	deviceOptions: { id: string; serialNumber: string }[] = [];
	deviceOwnerOptions: { id: string; name: string }[] = [];
	accessedByCompanyOptions: { id: string; name: string }[] = [];
	accessedByUserOptions: {
		id: string;
		firstName: string;
		lastName: string;
		email: string;
		displayName: string;
	}[] = [];
	propertyOptions: { id: string; name: string }[] = [];
	firstNames?: Client[] = [];
	lastNames?: Client[] = [];
	clientIdns?: Client[] = [];

	activeCompanyId = '';
	isSentrilock = false;
	isCareCompany = false;

	queryParams: QueryParam[] = [];

	deviceIds: string[] = [];
	deviceOwnersCompanyIds: string[] = [];
	accessedByCompanyIds: string[] = [];
	accessedByUserIds: string[] = [];
	propertyIds: string[] = [];
	firstName: string[] = [];
	lastName: string[] = [];
	clientIdn: string[] = [];

	deviceMultiSelectorSearchPlaceHolder = $localize`:@@accessReportDialog-deviceMultiSelectorSearchPlaceHolder:Search Serial Numbers (optional)`;
	// deviceMultiSelectorSearchPlaceHolder = $localize`:@@accessReportDialog-deviceMultiSelectorRequiredSearchPlaceHolder:Search Serial Numbers (required)`;
	deviceOwnerMultiSelectorSearchPlaceHolder = $localize`:@@accessReportDialog-deviceOwnerMultiSelectorSearchPlaceHolder:Search Companies (optional)`;
	accessedByCompanyMultiSelectorSearchPlaceHolder = $localize`:@@accessReportDialog-accessedByCompanyMultiSelectorSearchPlaceHolder:Search Companies (optional)`;
	accessedByUserMultiSelectorSearchPlaceHolder = $localize`:@@accessReportDialog-accessedByUserMultiSelectorSearchPlaceHolder:Search Users (optional)`;
	propertyMultiSelectorSearchPlaceHolder = $localize`:@@accessReportDialog-propertyMultiSelectorSearchPlaceHolder:Search Properties (optional)`;
	firstNameMultiSelectSearchPlaceholder = $localize`:@@inventoryReportDialog-firstNameMultiSelectSearchPlaceholder: Search Client First Names (optional)`;
	lastNameMultiSelectSearchPlaceholder = $localize`:@@inventoryReportDialog-lastNameMultiSelectSearchPlaceholder: Search Client Last Names (optional)`;
	clientIdnMultiSelectSearchPlaceholder = $localize`:@@inventoryReportDialog-clientIdnMultiSelectSearchPlaceholder: Search Client IDNs (optional)`;

	private readonly searchConfigs: { [key: string]: SearchConfig } = {
		device: {
			type: 'device',
			searchEvents: new BehaviorSubject<string>(''),
			subscription: new Subscription(),
			selectedItems: new Set(),
			options: this.deviceOptions || [],
			apiCall: (params) => this.apiService.getAllDevices(params),
			mapResponse: (device) => ({
				id: device.device.id,
				serialNumber: device.device.serialNumber,
			}),
			updateFilters: (items) => {
				this.accessReportFilters.deviceIds = [...items];
			},
		},
		deviceOwner: {
			type: 'deviceOwner',
			searchEvents: new BehaviorSubject<string>(''),
			subscription: new Subscription(),
			selectedItems: new Set(),
			options: this.deviceOwnerOptions || [],
			apiCall: (params) => this.apiService.getCompanyFilterOptions(params),
			mapResponse: (company) => ({
				id: company.id,
				name: company.name,
			}),
			updateFilters: (items) => {
				this.accessReportFilters.deviceOwnerCompanyIds = [...items];
			},
		},
		company: {
			type: 'company',
			searchEvents: new BehaviorSubject<string>(''),
			subscription: new Subscription(),
			selectedItems: new Set(),
			options: this.accessedByCompanyOptions || [],
			apiCall: (params) => this.apiService.getCompanyFilterOptions(params),
			mapResponse: (company) => ({
				id: company.id,
				name: company.name,
			}),
			updateFilters: (items) => {
				this.accessReportFilters.accessedByCompanyIds = [...items];
			},
		},
		user: {
			type: 'user',
			searchEvents: new BehaviorSubject<string>(''),
			subscription: new Subscription(),
			selectedItems: new Set(),
			options: this.accessedByUserOptions || [],
			apiCall: (params) =>
				this.apiService.getCompanyUsersByCompanyId(
					this.activeCompanyId,
					params
				),
			mapResponse: (user) => ({
				id: user.user.id,
				firstName: user.user.firstName,
				lastName: user.user.lastName,
				email: user.user.email,
				displayName: `${user.user.firstName} ${user.user.lastName} (${user.user.email})`,
			}),
			updateFilters: (items) => {
				this.accessReportFilters.accessedByUserIds = [...items];
			},
		},
		property: {
			type: 'property',
			searchEvents: new BehaviorSubject<string>(''),
			subscription: new Subscription(),
			selectedItems: new Set(),
			options: this.propertyOptions || [],
			apiCall: (params) => this.apiService.getProperties(params),
			mapResponse: (property) => ({
				id: property.property.id,
				name: property.property.name,
			}),
			updateFilters: (items) => {
				this.accessReportFilters.propertyIds = [...items];
			},
		},
		firstName: {
			type: 'firstName',
			searchEvents: new BehaviorSubject<string>(''),
			subscription: new Subscription(),
			selectedItems: new Set(),
			options: this.firstNames || [],
			apiCall: (params) =>
				this.apiService.getClients(this.activeCompanyId, params),
			mapResponse: (client) => client,
			updateFilters: (items) => {
				this.accessReportFilters.firstName = [...items];
			},
		},
		lastName: {
			type: 'lastName',
			searchEvents: new BehaviorSubject<string>(''),
			subscription: new Subscription(),
			selectedItems: new Set(),
			options: this.lastNames || [],
			apiCall: (params) =>
				this.apiService.getClients(this.activeCompanyId, params),
			mapResponse: (client) => client,
			updateFilters: (items) => {
				this.accessReportFilters.lastName = [...items];
			},
		},
		clientIdn: {
			type: 'clientIdn',
			searchEvents: new BehaviorSubject<string>(''),
			subscription: new Subscription(),
			selectedItems: new Set(),
			options: this.clientIdns || [],
			apiCall: (params) =>
				this.apiService.getClients(this.activeCompanyId, params),
			mapResponse: (client) => client,
			updateFilters: (items) => {
				this.accessReportFilters.clientIdn = [...items];
			},
		},
	};

	constructor(
		private readonly dialogRef: MatDialogRef<AccessReportDialogComponent>,
		private readonly apiService: ApiService,
		private readonly userProfileService: UserProfileService,
		private readonly router: Router,
		@Inject(MAT_DIALOG_DATA)
		public data?: {
			reportFilters: AccessReportFilters;
			filterOptions: AccessReportFilterOptions;
		}
	) {
		this.initializeData();
		Object.keys(this.searchConfigs).forEach((key) => this.setupSearch(key));
	}

	private initializeData(): void {
		this.filterOptions = this.data?.filterOptions;
		if (this.data?.reportFilters) {
			this.accessReportFilters = this.data.reportFilters;
		} else {
			this.accessReportFilters.visitStartDate = defaultAccessReportStartDate();
		}
		if (this.data?.reportFilters) {
			// Initialize deviceOptions with previous selections
			if (this.accessReportFilters.deviceIds?.length) {
				this.accessReportFilters.deviceIds.forEach((device) => {
					const deviceOption = {
						id: device.id,
						serialNumber: device.serialNumber,
					};
					this.deviceIds.push(device.id);
					this.searchConfigs.device.selectedItems.add(deviceOption);
				});

				// Initialize deviceOptions with selected devices
				this.deviceOptions = Array.from(
					this.searchConfigs.device.selectedItems
				);
			}

			// Initialize deviceOwnerOptions with previous selections
			if (this.accessReportFilters.deviceOwnerCompanyIds?.length) {
				this.accessReportFilters.deviceOwnerCompanyIds.forEach((company) => {
					const companyOption = {
						id: company.id,
						name: company.name,
					};
					this.deviceOwnersCompanyIds.push(company.id);
					this.searchConfigs.deviceOwner.selectedItems.add(companyOption);
				});
				// Initialize deviceOwnerOptions with selected companies
				this.deviceOwnerOptions = Array.from(
					this.searchConfigs.deviceOwner.selectedItems
				);
			}

			// Initialize accessedByCompanyOptions with previous selections
			if (this.accessReportFilters.accessedByCompanyIds?.length) {
				this.accessReportFilters.accessedByCompanyIds.forEach((company) => {
					const companyOption = {
						id: company.id,
						name: company.name,
					};
					this.accessedByCompanyIds.push(company.id);
					this.searchConfigs.company.selectedItems.add(companyOption);
				});
				// Initialize accessedByCompanyOptions with selected companies
				this.accessedByCompanyOptions = Array.from(
					this.searchConfigs.company.selectedItems
				);
			}

			// Initialize accessedByUserOptions with previous selections
			if (this.accessReportFilters.accessedByUserIds?.length) {
				this.accessReportFilters.accessedByUserIds.forEach((user) => {
					const userOption = {
						id: user.id,
						firstName: user.firstName,
						lastName: user.lastName,
						email: user.email,
						displayName: `${user.firstName} ${user.lastName} (${user.email})`,
					};
					this.accessedByUserIds.push(user.id);
					this.searchConfigs.user.selectedItems.add(userOption);
				});
				// Initialize accessedByUserOptions with selected users
				this.accessedByUserOptions = Array.from(
					this.searchConfigs.user.selectedItems
				);
			}

			// Initialize propertyOptions with previous selections
			if (this.accessReportFilters.propertyIds?.length) {
				this.accessReportFilters.propertyIds.forEach((property) => {
					const propertyOption = {
						id: property.id,
						name: property.name,
					};
					this.propertyIds.push(property.id);
					this.searchConfigs.property.selectedItems.add(propertyOption);
				});
				// Initialize propertyOptions with selected properties
				this.propertyOptions = Array.from(
					this.searchConfigs.property.selectedItems
				);
			}
		}

		this.checkCareCompany()
			.then(() => {})
			.catch((error) =>
				console.error(
					'Something went wrong while checking care company:',
					error.message
				)
			);
	}

	async checkCareCompany(): Promise<void> {
		const userProfile = await this.userProfileService.getUserProfile();
		this.isCareCompany =
			userProfile?.activeRoleDetails?.company?.companyType === 'care';
	}

	private setupSearch(type: string): void {
		const config = this.searchConfigs[type];
		const searchResults = config.searchEvents.asObservable().pipe(
			debounce(() => interval(300)),
			mergeMap((searchTerm) => this.handleSearch(searchTerm, config))
		);

		config.subscription = searchResults.subscribe();
	}

	private handleSearch(
		searchTerm: string,
		config: SearchConfig
	): Observable<any[]> {
		if (['firstName', 'lastName', 'clientIdn'].includes(config.type)) {
			if (!searchTerm || searchTerm.length < 2) {
				switch (config.type) {
					case 'firstName':
						return of(this.firstNames || []);
					case 'lastName':
						return of(this.lastNames || []);
					case 'clientIdn':
						return of(this.clientIdns || []);
				}
			}
		} else {
			if (!searchTerm || searchTerm === '' || searchTerm.length < 3) {
				switch (config.type) {
					case 'device':
						return of(this.deviceOptions);
					case 'company':
						return of(this.accessedByCompanyOptions);
					case 'user':
						return of(this.accessedByUserOptions);
					case 'property':
						return of(this.propertyOptions);
					case 'deviceOwner':
						return of(this.deviceOwnerOptions);
				}
			}
		}

		const queryParameters: QueryParam[] = [
			{ param: this.getSearchParamName(config), value: searchTerm },
			{ param: 'rowIndex', value: 0 },
			{ param: 'rowCount', value: 100 },
		];

		return from(config.apiCall(queryParameters)).pipe(
			map((results) => {
				if (!results?.length) {
					return Array.from(config.selectedItems);
				}

				const newOptions = results.map(config.mapResponse);
				const mergedOptions = this.mergeWithSelected(
					newOptions,
					config.selectedItems
				);

				// Update the corresponding options array based on the config type
				switch (config.type) {
					case 'device':
						this.deviceOptions = mergedOptions;
						break;
					case 'company':
						this.accessedByCompanyOptions = mergedOptions;
						break;
					case 'user':
						this.accessedByUserOptions = mergedOptions;
						break;
					case 'property':
						this.propertyOptions = mergedOptions;
						break;
					case 'deviceOwner':
						this.deviceOwnerOptions = mergedOptions;
						break;
					case 'firstName':
						this.firstNames = mergedOptions;
						break;
					case 'lastName':
						this.lastNames = mergedOptions;
						break;
					case 'clientIdn':
						this.clientIdns = mergedOptions;
						break;
				}

				return mergedOptions;
			})
		);
	}

	private getSearchParamName(config: SearchConfig): string {
		switch (config.type) {
			case 'device':
				return 'serialNumber';
			case 'company':
				return 'companyName';
			case 'user':
				return 'name';
			case 'property':
				return 'propertyName';
			case 'deviceOwner':
				return 'companyName';
			case 'firstName':
				return 'firstName';
			case 'lastName':
				return 'lastName';
			case 'clientIdn':
				return 'idn';
			default:
				return 'name';
		}
	}

	private mergeWithSelected(newOptions: any[], selectedItems: Set<any>): any[] {
		if (!newOptions) newOptions = [];
		if (!selectedItems) return newOptions;

		const newOptionIds = new Set(newOptions.map((opt) => opt.id));
		let mergedOptions = [...newOptions];

		selectedItems.forEach((selected) => {
			if (newOptionIds.has(selected.id)) {
				// if the selected item is already in the new options list, remove it from mergedOptions
				mergedOptions = mergedOptions.filter((opt) => opt.id !== selected.id);
			}
		});

		return mergedOptions;
	}

	async ngAfterViewInit(): Promise<void> {
		const userProfile: UserProfile = await this.apiService.getUserProfile();
		this.activeCompanyId = userProfile?.activeRoleDetails?.company?.id ?? '';
		const sentrilockId = await this.apiService.getSentrilockCompanyId();
		this.isSentrilock = this.activeCompanyId === sentrilockId;
	}

	get hasFilters(): boolean {
		return (
			JSON.stringify(this.accessReportFilters) !==
			JSON.stringify(emptyAccessReportFilters())
		);
	}

	get primaryButtonText(): string {
		return this.hasFilters
			? $localize`:@@accessReportDialog-generate:Generate`
			: $localize`:@@accessReportDialog-generateWithoutFilters:Generate without Filters`;
	}

	onSelectedItemsChanged(type: string, items: any): void {
		if (!items) return;

		const config = this.searchConfigs[type];
		config.selectedItems = new Set(items);
		config.updateFilters(items);
		config.searchEvents.next('');
	}

	onStartDateChanged(event: MatDatepickerInputEvent<Date>): void {
		this.accessReportFilters.visitStartDate = event.value ?? undefined;
	}

	onEndDateChanged(event: MatDatepickerInputEvent<Date>): void {
		this.accessReportFilters.visitEndDate = event.value ?? undefined;
	}

	onCloseDialog(): void {
		this.dialogRef.close();
	}

	async onGenerateReport(): Promise<void> {
		const compiledFilters = this.compileFilters();
		this.router
			.navigateByUrl(`/reports/accessReport`, {
				state: {
					reportFilters: this.accessReportFilters,
					compiledFilters,
					hasFilters: this.hasFilters,
					filterOptions: this.filterOptions,
				},
			})
			.then(() => {
				this.dialogRef.close();
			});
	}

	compileFilters(): QueryParam[] {
		const queryParameters: QueryParam[] = [];

		if (
			this.accessReportFilters.deviceIds &&
			this.accessReportFilters.deviceIds.length > 0
		) {
			queryParameters.push({
				param: 'deviceIds',
				value: this.accessReportFilters.deviceIds.map((it) => it.id),
			});
		}
		if (
			this.accessReportFilters.deviceOwnerCompanyIds &&
			this.accessReportFilters.deviceOwnerCompanyIds.length > 0
		) {
			queryParameters.push({
				param: 'deviceOwnersCompanyIds',
				value: this.accessReportFilters.deviceOwnerCompanyIds.map(
					(it) => it.id
				),
			});
		}
		if (
			this.accessReportFilters.accessedByCompanyIds &&
			this.accessReportFilters.accessedByCompanyIds.length > 0
		) {
			queryParameters.push({
				param: 'accessedByCompanyIds',
				value: this.accessReportFilters.accessedByCompanyIds.map((it) => it.id),
			});
		}
		if (
			this.accessReportFilters.accessedByUserIds &&
			this.accessReportFilters.accessedByUserIds.length > 0
		) {
			queryParameters.push({
				param: 'accessedByUserIds',
				value: this.accessReportFilters.accessedByUserIds.map((it) => it.id),
			});
		}
		if (
			this.accessReportFilters.propertyIds &&
			this.accessReportFilters.propertyIds.length > 0
		) {
			queryParameters.push({
				param: 'propertyIds',
				value: this.accessReportFilters.propertyIds.map((it) => it.id),
			});
		}
		if (this.accessReportFilters.visitStartDate) {
			queryParameters.push({
				param: 'visitStartDate',
				value: this.accessReportFilters.visitStartDate
					.toISOString()
					.split('T')[0],
			});
		}
		if (this.accessReportFilters.visitEndDate) {
			queryParameters.push({
				param: 'visitEndDate',
				value: this.accessReportFilters.visitEndDate
					.toISOString()
					.split('T')[0],
			});
		}
		if (
			this.accessReportFilters.firstName &&
			this.accessReportFilters.firstName.length > 0
		) {
			queryParameters.push({
				param: 'firstNames',
				value: this.accessReportFilters.firstName.map(
					(it) => it.firstName || ''
				),
			});
		}

		if (
			this.accessReportFilters.lastName &&
			this.accessReportFilters.lastName.length > 0
		) {
			queryParameters.push({
				param: 'lastNames',
				value: this.accessReportFilters.lastName.map((it) => it.lastName || ''),
			});
		}

		if (
			this.accessReportFilters.clientIdn &&
			this.accessReportFilters.clientIdn.length > 0
		) {
			queryParameters.push({
				param: 'clientIdns',
				value: this.accessReportFilters.clientIdn.map((it) => it.idn || ''),
			});
		}

		return queryParameters;
	}

	onSearchInput(type: string, searchTerm: string): void {
		this.searchConfigs[type].searchEvents.next(searchTerm);
	}

	ngOnDestroy(): void {
		Object.values(this.searchConfigs).forEach((config) => {
			if (config.subscription) {
				config.subscription.unsubscribe();
			}
		});
	}
}
